import { fetchAuthSession } from 'aws-amplify/auth';
import { signRequest } from '@aws-amplify/core/internals/aws-client-utils';

const TIMEOUT = 500000;

/**
 * Creates a promise that rejects after a specified timeout
 */
function timeoutPromise<T>(ms: number, promise: Promise<T>): Promise<T> {
  const timeout = new Promise<T>((_, reject) =>
    setTimeout(() => reject(new Error('Request timed out')), ms)
  );
  return Promise.race([promise, timeout]);
}

/**
 * Signs a request for Lambda function URLs using AWS SigV4
 */
async function signApiRequest(url: string, method: string, body?: any) {
  // Get the current credentials
  const session = await fetchAuthSession();

  // Ensure credentials exist
  if (!session.credentials) {
    throw new Error('No credentials available');
  }

  // Convert string URL to URL object
  const urlObj = new URL(url);

  // Extract region from URL (lambda-url.us-west-2.on.aws)
  const region = urlObj.hostname.split('.')[2] || 'us-west-2';

  // Sign the request with SigV4
  const signed = await signRequest(
    {
      method,
      url: urlObj,
      headers: { 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    },
    {
      credentials: session.credentials,
      signingRegion: region,
      signingService: 'lambda',
    }
  );

  return {
    headers: signed.headers,
    body: signed.body,
  };
}

/**
 * Handles a fetch request to a Lambda function URL with proper signing
 */
async function fetchLambdaUrl(
  url: string,
  method: string,
  options: {
    timeoutDuration: number;
    headers?: HeadersInit;
    body?: any;
  }
) {
  const { timeoutDuration, headers = {}, body } = options;
  const signedRequest = await signApiRequest(url, method, body);

  const requestInit: RequestInit = {
    method,
    headers: { ...headers, ...signedRequest.headers },
  };

  if (body) {
    requestInit.body = body instanceof File ? body : JSON.stringify(body);
  }

  return timeoutPromise(timeoutDuration, fetch(url, requestInit));
}

/**
 * Handles a standard fetch request
 */
async function fetchStandard(
  url: string,
  method: string,
  options: {
    timeoutDuration: number;
    headers?: HeadersInit;
    body?: any;
  }
) {
  const { timeoutDuration, headers = {}, body } = options;

  const requestInit: RequestInit = {
    method,
    headers: { 'Content-Type': 'application/json', ...headers },
  };

  if (body) {
    requestInit.body = body instanceof File ? body : JSON.stringify(body);
  }

  return timeoutPromise(timeoutDuration, fetch(url, requestInit));
}

/**
 * Handles error responses.
 *
 * Attempts to parse the response body as JSON. If successful, attaches it as
 * `responseBody` on the thrown Error so callers can inspect structured payloads
 * (e.g. publishListingLambda 502 + `{ listingId, publishError }`).
 *
 * Pulls the human-readable reason from the body in this order:
 *   1. `message` — used by lambdas that throw a `BaseError` (httpUtil pattern)
 *   2. `error`   — used by lambdas that build their own JSON.stringify error
 *                  body (productFormatsCrudLambda, listingsCrudLambda, etc.)
 * Both shapes coexist across the service today; the frontend tolerates either
 * so toasts surface the actual reason instead of "Failed to post data."
 */
async function handleErrorResponse(response: Response, defaultMessage: string): Promise<never> {
  let responseBody: unknown;
  try {
    responseBody = await response.json();
  } catch {
    throw new Error(defaultMessage);
  }
  const body = responseBody as Record<string, unknown> | null;
  const reasonField = body?.message ?? body?.error;
  const message = reasonField ? String(reasonField) || defaultMessage : defaultMessage;
  const err = new Error(message) as Error & { responseBody?: unknown };
  err.responseBody = responseBody;
  throw err;
}

export async function get(url: string, timeoutDuration = TIMEOUT): Promise<any> {
  try {
    const isLambdaUrl = url.includes('.lambda-url.');
    const response = isLambdaUrl
      ? await fetchLambdaUrl(url, 'GET', { timeoutDuration })
      : await fetchStandard(url, 'GET', { timeoutDuration });

    if (!response.ok) {
      await handleErrorResponse(response, 'Failed to fetch data.');
    }

    return await response.json();
  } catch (error) {
    console.error('There was an issue with fetching data:', error);
    throw error;
  }
}

export async function post(
  url: string,
  data: any,
  options: { headers?: HeadersInit; timeoutDuration?: number } = {}
): Promise<any> {
  const { headers, timeoutDuration = TIMEOUT } = options;
  try {
    const isLambdaUrl = url.includes('.lambda-url.');
    const response = isLambdaUrl
      ? await fetchLambdaUrl(url, 'POST', { timeoutDuration, headers, body: data })
      : await fetchStandard(url, 'POST', { timeoutDuration, headers, body: data });

    if (!response.ok) {
      await handleErrorResponse(response, 'Failed to post data.');
    }

    return await response.json();
  } catch (error) {
    console.error('There was an issue with posting data:', error);
    throw error;
  }
}

export async function put(
  url: string,
  data: any,
  headers: HeadersInit,
  timeoutDuration = TIMEOUT
): Promise<any> {
  try {
    const isLambdaUrl = url.includes('.lambda-url.');
    const response = isLambdaUrl
      ? await fetchLambdaUrl(url, 'PUT', { timeoutDuration, headers, body: data })
      : await fetchStandard(url, 'PUT', { timeoutDuration, headers, body: data });

    if (!response.ok) {
      throw new Error(`Failed to put data. Status: ${response.status}`);
    }

    return await response;
  } catch (error) {
    console.error('There was an issue with putting data:', error);
    throw error;
  }
}
